import { api } from '@/api';
import { createEventHook } from '@vueuse/core';
import { DateTime } from 'luxon';
import {
  computed,
  MaybeRefOrGetter,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  toValue,
  watch,
} from 'vue';
import { DISABLE_BEFOREUNLOAD_WARNING } from '@/env';
import { objectFromKeys } from '../helper';
import { mapValues } from 'lodash';
import { EntityLockTokenDto } from '@/api/generator';
import { stopErrorPropagation } from '@/errorHandling';

export type EntityWithToken = { id?: string | number; $type?: string };

export const tokenEvents = [
  'requested',
  'revoked',
  'renewed',
  'inUse',
  'noConnection',
  'initialized',
] as const;
export type TokenEvent = (typeof tokenEvents)[number];

export function hasIdAndType(entity?: any): entity is Required<EntityWithToken> {
  return entity.id !== undefined && entity.$type !== undefined;
}

function isTokenExpired(token: EntityLockTokenDto) {
  return (
    DateTime.fromISO(token.lastRequest).plus({ seconds: token.renewalTimeout }) < DateTime.now()
  );
}

async function requestToken(entity: Required<EntityWithToken>, signal: AbortSignal) {
  return await api.entityLockTokensApi.requestToken(
    {
      entityLockTokenInputDto: {
        entityId: entity.id.toString(),
        entityType: entity.$type,
      },
    },
    [stopErrorPropagation],
    { signal },
  );
}

async function renewToken(tokenId: string, signal?: AbortSignal) {
  return await api.entityLockTokensApi.updateToken(
    {
      tokenId,
    },
    [stopErrorPropagation],
    { signal },
  );
}

/**
 * Revoke is best-effort cleanup on unmount or navigation, so every failure is suppressed rather
 * than reaching `defaultErrorHandler`: the server expires stale locks on its own, and the user
 * is already leaving the view. The caller still reacts to 409 and connection errors via the
 * returned `error`.
 */
async function revokeToken(tokenId: string) {
  return await api.entityLockTokensApi.deleteToken(
    {
      tokenId,
    },
    [stopErrorPropagation],
  );
}

function getReducedEntityWithToken(entity: EntityWithToken | undefined) {
  const entityRef = ref<Required<EntityWithToken> | undefined>(undefined);

  watch(
    () => ({ id: entity?.id, $type: entity?.$type }),
    (entityWithToken) => {
      entityRef.value = hasIdAndType(entityWithToken) ? entityWithToken : undefined;
    },
    {
      immediate: true,
    },
  );

  return entityRef;
}

export function useToken(entity: MaybeRefOrGetter<EntityWithToken | undefined>) {
  const retryIntervalSeconds = 5;

  const entityRef = getReducedEntityWithToken(toValue(entity));

  const currentToken = ref<EntityLockTokenDto>();

  const inUse = ref<boolean>(false);

  let repeat: ReturnType<typeof setTimeout> | undefined = undefined;

  const currentRenewalInterval = computed(() =>
    currentToken.value?.renewalTime ? currentToken.value.renewalTime * 1000 : undefined,
  );

  const isValid = computed(() => !!currentToken.value && !isTokenExpired(currentToken.value));

  const onUpdateHook = createEventHook<{ event: TokenEvent; token?: EntityLockTokenDto }>();

  const tokenEventHooks = objectFromKeys(
    [...tokenEvents],
    createEventHook<EntityLockTokenDto | undefined>,
  );

  function triggerUpdate(event: TokenEvent, token = currentToken.value) {
    onUpdateHook.trigger({ event, token });
    tokenEventHooks[event].trigger(token);
  }

  const requestController = new AbortController();

  async function request(
    renewal: 'manual' | 'automatic' = 'automatic',
    retryInterval = retryIntervalSeconds * 1000,
  ) {
    if (!entityRef.value) {
      // when entity not set yet wait for it and try again one more time
      watch(entityRef, async () => await request(renewal, retryInterval), { once: true });
      return;
    }

    const oldToken = currentToken.value;

    const {
      success,
      result: newToken,
      error,
    } = await requestToken(entityRef.value, requestController.signal);

    if (success) {
      currentToken.value = newToken;
      triggerUpdate('requested');

      if (!oldToken) {
        triggerUpdate('initialized');
      }

      if (renewal === 'automatic' && !repeat) {
        await renew('afterRenewalTime');
      }
    } else {
      switch (error.name) {
        case 'ErrorResponseOfPtw100ConflictException': {
          triggerUpdate('inUse');
          inUse.value = true;
          break;
        }

        case 'FetchError': {
          triggerUpdate('noConnection');
          repeat = setTimeout(async () => await request(renewal), retryInterval);
          break;
        }
      }
    }
  }

  async function renew(
    start: 'immediate' | 'afterRenewalTime' = 'immediate',
    retryInterval = retryIntervalSeconds * 1000,
  ) {
    if (!currentToken.value) {
      return;
    }

    if (start === 'afterRenewalTime') {
      clearTimeout(repeat);
      repeat = setTimeout(async () => await renew('immediate'), currentRenewalInterval.value);
      return;
    }

    const {
      success,
      result: updatedToken,
      error,
    } = await renewToken(currentToken.value.id, requestController.signal);

    if (success) {
      currentToken.value = updatedToken;

      triggerUpdate('renewed');

      await renew('afterRenewalTime');
    } else {
      switch (error.name) {
        case 'ErrorResponseOfPtw100ConflictException': {
          triggerUpdate('inUse');
          inUse.value = true;
          break;
        }

        case 'FetchError': {
          triggerUpdate('noConnection');
          repeat = setTimeout(async () => await renew(start), retryInterval);
          break;
        }

        default: {
          request('automatic');
        }
      }
    }
  }

  async function revoke() {
    requestController.abort();
    clearTimeout(repeat);

    const tokenId = currentToken.value?.id;

    if (!tokenId) {
      return;
    }

    const { success, error } = await revokeToken(tokenId);
    currentToken.value = undefined;

    if (success) {
      inUse.value = false;
      triggerUpdate('revoked');
    } else {
      switch (error.name) {
        case 'ErrorResponseOfPtw100ConflictException': {
          triggerUpdate('inUse');
          inUse.value = true;
          break;
        }

        case 'FetchError': {
          triggerUpdate('noConnection');
          break;
        }
      }
    }
  }

  watch(entityRef, async (_newEntity, oldEntity) => {
    if (oldEntity) {
      await revoke();
    }
  });

  async function beforeUnloadHandler(ev: BeforeUnloadEvent) {
    if (DISABLE_BEFOREUNLOAD_WARNING) {
      ev.stopImmediatePropagation();
    }

    await revoke();
  }

  onBeforeMount(() => {
    window.addEventListener('beforeunload', beforeUnloadHandler);
  });

  onBeforeUnmount(async () => {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    await revoke();
  });

  return {
    request,
    revoke,
    renew,
    onUpdate: onUpdateHook.on,
    on: mapValues(tokenEventHooks, (hook) => hook.on),
    isValid,
    inUse,
    currentToken,
  };
}
